import React from 'react';
import {Route, Router, Switch} from 'react-router-dom';
import PropTypes from 'prop-types';
import {CallbackComponent, createUserManager, OidcProvider, processSilentRenew, SignoutCallbackComponent} from "redux-oidc";
import OIDCOptions from "./OIDCOptions";
import {objectAtPath, pathToParts} from "../../index";
import setupOIDCReduxReducer from "./setupOIDCReduxReducer";

const propTypes = {
	// what to show if not doing oidc login urls; ie your actual App
	contentIfLoggedIn: PropTypes.func.isRequired,

	// while logging in, can override the default "Logging In..." text with any custom content
	// this is a function so it can lazy render when needed
	contentIfLoggingIn: PropTypes.func,

	// react router history
	history: PropTypes.object.isRequired,

	// react router location
	location: PropTypes.object.isRequired,

	// after logging out through UMD, the user will be redirected back to this location in your app
	logoutDestinationUrl: PropTypes.string,

	// callback when an error occurs during log in: error => { whatever you want }
	onLoginError: PropTypes.func,

	// callback when the user logs in: user => { whatever you want }; defaults to going to last known app url after login
	onLoginSuccess: PropTypes.func,

	// callback when the user logs out: user => { whatever you want }
	onLogoutSuccess: PropTypes.func,

	// callback when an error occurs during signout in: error => { whatever you want }
	onSignoutError: PropTypes.func,

	// callback to get the usermanager after it is loaded
	onUserManagerLoaded: PropTypes.func,

	// css style for messages like "Logging in..." "Renewing Login..."
	messageClassName: PropTypes.string,

	// auto setup reducers for handling openid user management
	reduxReducers: PropTypes.object,

	// openid user will be kept in Redux Store
	reduxStore: PropTypes.object.isRequired,
};

const defaultProps = {
	contentIfLoggingIn: undefined,
	logoutDestinationUrl: undefined,
	onLoginError: undefined,
	onLoginSuccess: undefined,
	onLogoutSuccess: undefined,
	onSignoutError: undefined,
	onUserManagerLoaded: undefined,
	messageClassName: undefined,
	reduxReducers: undefined,
};

export default class OIDCCore extends React.Component {

	constructor(props) {
		super(props);

		// track so that rerendering doesn't go to umd logout url at every rerender
		this.loggedOut = false;

		// creating in constructor means that if userManagerConfig properties change after component is created, it will not have an effect
		// BUT, creating a new user manager for every render seems WAY overkill, so going to try just doing once at front and see who complains

		// OIDCOptions not passed as props because it's a one time setup thing and changing dynamically during execution could have some crazy side effects
		if (!OIDCOptions.client_id) {
			// Probably in your App.js BEFORE rendering, do OIDCOptions.client_id = 'whateverApAdminSaysItIs'
			throw 'OIDCOptions must have client_id set before constructing an OIDCCore component';
		}

		this.userManagerConfig = {
			authority: OIDCOptions.loginUrl(),
			automaticSilentRenew: OIDCOptions.automaticSilentRenew,
			client_id: OIDCOptions.client_id,
			filterProtocolClaims: OIDCOptions.filterProtocolClaims,
			loadUserInfo: OIDCOptions.loadUserInfo,
			redirect_uri: OIDCOptions.urlWithContext(OIDCOptions.paths.loginCallback()),
			response_type: OIDCOptions.responseType,
			scope: OIDCOptions.scope,
			silent_redirect_uri: OIDCOptions.urlWithContext(OIDCOptions.paths.silentRenew()),
		};

		this.userManager = createUserManager(this.userManagerConfig);
		this.props.onUserManagerLoaded && this.props.onUserManagerLoaded(this.userManager);

		props.reduxReducers && setupOIDCReduxReducer(props.reduxReducers);

		// add oidc user storage in redux store if it is missing (make sure the holding object exists so that storing works)
		const {path} = pathToParts(OIDCOptions.reduxUserPath);
		if (!objectAtPath(props.reduxStore.getState(), path)) {
			const parts = OIDCOptions.reduxUserPath.split(/\./);
			parts.slice(0, -1)
				.map((field, i) => ({type: 'UPDATE_DATA', payload: {path: parts.slice(0, i).join('.'), field, value: {}}}))
				.forEach(props.reduxStore.dispatch);
		}
	}

	renderAppContent = () => {
		localStorage.setItem('appLastLocation', this.props.location.pathname);
		return this.props.contentIfLoggedIn();
	};

	renderSecuredContent = () => (
		<OidcProvider store={this.props.reduxStore} userManager={this.userManager}>
			<Router history={this.props.history}>
				<Switch>
					{/* Do Login*/}
					<Route path={OIDCOptions.paths.loginCallback()} render={() => (
						<CallbackComponent
							userManager={this.userManager}
							successCallback={user => {
								const lastPath = localStorage.getItem('appLastLocation') || '/';
								if (!this.props.onLoginSuccess || this.props.onLoginSuccess(user, lastPath)) {
									this.props.history.push(lastPath);
								}
							}}
							errorCallback={this.props.onLoginError}
						>
							<div className={this.props.messageClassName}>
								{
									this.props.contentIfLoggingIn ?
										this.props.contentIfLoggingIn() :
										'Logging in...'
								}
							</div>
						</CallbackComponent>
					)}/>

					{/* Do Logout */}
					<Route path={OIDCOptions.paths.logout()} render={() => (
						<SignoutCallbackComponent
							userManager={this.userManager}
							successCallback={user => {
								this.userManager.removeUser()
									.then(() => {
										if (!this.loggedOut) {
											this.loggedOut = true;
											if (this.props.onLogoutSuccess) {
												this.props.onLogoutSuccess();
											} else {
												window.location = OIDCOptions.logoutUrl(this.props.logoutDestinationUrl);
											}
										}
									});
							}}
							errorCallback={this.props.onSignoutError}
						>
							<div className={this.props.messageClassName}>Logging out...</div>
						</SignoutCallbackComponent>
					)}/>

					{/* Do Silent Renew */}
					<Route path={OIDCOptions.paths.silentRenew()} render={() => {
						processSilentRenew();
						return <div className={this.props.messageClassName}>Renewing Login...</div>;
					}}/>

					{/* Make sure logged in and forward on to the actual app */}
					<Route render={() => {
						const oidcUser = objectAtPath(this.props.reduxStore.getState(), OIDCOptions.reduxUserPath);
						const isLoggedIn = (oidcUser && !oidcUser.expired);
						let result;
						if (!isLoggedIn) {
							localStorage.setItem('appLastLocation', this.props.location.pathname);
							this.userManager.signinRedirect();
							result = null;
						} else {
							result = this.renderAppContent();
						}
						return result;
					}}/>
				</Switch>
			</Router>
		</OidcProvider>
	);

	render() {
		return (
			<Router history={this.props.history}>
				<Switch>
					{OIDCOptions.oidcSecuredPaths.map(path => <Route key={path} path={path} render={this.renderSecuredContent}/>)}

					{/* Not a secured path, so nothing for OIDC to do, just forward on to the actual app*/}
					<Route render={this.renderAppContent}/>
				</Switch>
			</Router>
		);
	}
}

OIDCCore.propTypes = propTypes;
OIDCCore.defaultProps = defaultProps;
